import type { AxiosInstance } from 'axios';

import axiosImport from './api';

const axios = axiosImport as unknown as AxiosInstance;

interface CreateVideoBookmarkOptions {
  end_ts: number;
  item_indices: number[];
  questionId: number;
  responseId: number;
  signal?: AbortSignal;
  start_ts: number;
  text: string;
}

interface CreateVideoFavoriteOptions {
  questionId: number;
  responseId: number;
  signal?: AbortSignal;
}

interface DeleteVideoBookmarkOptions {
  bookmarkId: number;
  signal?: AbortSignal;
}

interface DeleteVideoFavoriteOptions {
  favoriteId: number;
  signal?: AbortSignal;
}

interface ListVideoBookmarksResponse {
  count?: number;
  next?: null | string;
  previous?: null | string;
  results: VideoBookmark[];
}

interface ListVideoBookmarksResponseOptions {
  questionId: number;
  responseId: number;
  signal?: AbortSignal;
}

interface ListVideoBookmarksSurvey {
  count?: number;
  next?: null | string;
  previous?: null | string;
  results: VideoBookmark[];
}

interface ListVideoBookmarksSurveyOptions {
  signal?: AbortSignal;
  surveyRef: number;
}

interface ListVideoFavoritesSurvey {
  count?: number;
  next?: null | string;
  previous?: null | string;
  results: VideoFavorite[];
}

interface ListVideoFavoritesSurveyOptions {
  signal?: AbortSignal;
  surveyRef: number;
}

interface VideoBookmark {
  created_at?: string;
  end_ts: number;
  id: number;
  item_indices: number[];
  start_ts: number;
  survey_response: number;
  text: string;
  updated_at?: string;
}

interface VideoFavorite {
  created_at?: string;
  id: number;
  question_id: number;
  survey_response: number;
  updated_at?: string;
}

/**
 * List video bookmarks for a survey
 * @param {Object} options
 * @param {number} options.surveyRef - The survey response ID
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<ListVideoBookmarksSurvey | VideoBookmark[]>}
 */
export const listVideoBookmarksSurvey = async ({
  signal,
  surveyRef,
}: ListVideoBookmarksSurveyOptions): Promise<ListVideoBookmarksSurvey | VideoBookmark[]> => {
  const { data } = await axios({
    method: 'get',
    params: {
      survey: surveyRef,
    },
    signal,
    url: '/survey/response/video_bookmark/',
  });
  return data as ListVideoBookmarksSurvey | VideoBookmark[];
};

/**
 * List video bookmarks for a survey response
 * @param {Object} options
 * @param {number} options.questionId - The question ID
 * @param {number} options.responseId - The survey response ID
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<ListVideoBookmarksResponse | VideoBookmark[]>}
 */
export const listVideoBookmarksResponse = async ({
  questionId,
  responseId,
  signal,
}: ListVideoBookmarksResponseOptions): Promise<ListVideoBookmarksResponse | VideoBookmark[]> => {
  const { data } = await axios({
    method: 'get',
    params: {
      question_id: questionId,
      survey_response: responseId,
    },
    signal,
    url: '/survey/response/video_bookmark/',
  });
  return data as ListVideoBookmarksResponse | VideoBookmark[];
};

/**
 * Create a video bookmark
 * @param {Object} options
 * @param {number} options.end_ts - The end timestamp in seconds
 * @param {number[]} options.item_indices - Array of item indices
 * @param {number} options.questionId - The question ID
 * @param {number} options.responseId - The survey response ID
 * @param {AbortSignal} [options.signal]
 * @param {number} options.start_ts - The start timestamp in seconds
 * @param {string} options.text - The bookmarked text
 * @returns {Promise<VideoBookmark>}
 */
export const createVideoBookmark = async ({
  end_ts,
  item_indices,
  questionId,
  responseId,
  signal,
  start_ts,
  text,
}: CreateVideoBookmarkOptions): Promise<VideoBookmark> => {
  const { data } = await axios({
    data: {
      end_ts,
      item_indices,
      question_id: questionId,
      start_ts,
      survey_response: responseId,
      text,
    },
    method: 'post',
    signal,
    url: '/survey/response/video_bookmark/',
  });
  return data as VideoBookmark;
};

/**
 * Delete a video bookmark
 * @param {Object} options
 * @param {number} options.bookmarkId - The bookmark ID to delete
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<void>}
 */
export const deleteVideoBookmark = async ({
  bookmarkId,
  signal,
}: DeleteVideoBookmarkOptions): Promise<void> => {
  await axios({
    method: 'delete',
    signal,
    url: `/survey/response/video_bookmark/${bookmarkId}/`,
  });
};

/**
 * List video favorites for a survey
 * @param {Object} options
 * @param {number} options.surveyRef - The survey ID
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<ListVideoFavoritesSurvey | VideoFavorite[]>}
 */
export const listVideoFavoritesSurvey = async ({
  signal,
  surveyRef,
}: ListVideoFavoritesSurveyOptions): Promise<ListVideoFavoritesSurvey | VideoFavorite[]> => {
  const { data } = await axios({
    method: 'get',
    params: {
      survey: surveyRef,
    },
    signal,
    url: '/survey/response/video_favorite/',
  });
  return data as ListVideoFavoritesSurvey | VideoFavorite[];
};

/**
 * Create a video favorite
 * @param {Object} options
 * @param {number} options.responseId - The survey response ID
 * @param {number} options.questionId - The question ID
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<VideoFavorite>}
 */
export const createVideoFavorite = async ({
  questionId,
  responseId,
  signal,
}: CreateVideoFavoriteOptions): Promise<VideoFavorite> => {
  const { data } = await axios({
    data: {
      question_id: questionId,
      survey_response: responseId,
    },
    method: 'post',
    signal,
    url: '/survey/response/video_favorite/',
  });
  return data as VideoFavorite;
};

/**
 * Delete a video favorite
 * @param {Object} options
 * @param {number} options.favoriteId - The favorite ID to delete
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<void>}
 */
export const deleteVideoFavorite = async ({
  favoriteId,
  signal,
}: DeleteVideoFavoriteOptions): Promise<void> => {
  await axios({
    method: 'delete',
    signal,
    url: `/survey/response/video_favorite/${favoriteId}/`,
  });
};
